import { Button, RadioToggle, RollingAmount, SearchBar, Typography } from '@altify/components';
import { useContent } from '@altify/utils';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import Tabs from 'components/Tabs/Tabs';
import { TimeFrameKey, timeFrames } from 'data/time-frames';
import { dollarCurrencyCode, dollarCurrencySymbol } from 'utils/constants';
import { exploreProductsContent } from '../explore.content';
import GainLoss from 'components/GainLoss/GainLoss';
import { gainLossContent } from 'app/_authenticated/portfolio/gainLoss.content';
import { InfoButton } from 'components/InfoButton/InfoButton';
import Dropdown from 'components/Dropdown/Dropdown';
import { Pressable } from 'react-native';
import { ShowIcon } from '../assets/icons/ShowIcon';
import { HideIcon } from '../assets/icons/HideIcon';
import referDollar from '../assets/icons/refer_dollar.png';
import Modal from 'components/Modal/Modal';
import { useState } from 'react';
import RewardsShare from 'app/_authenticated/rewards/components/RewardsShare/RewardsShare';

function ProductsPortfolio({
  isSearching,
  portfolioValueFormatted,
  portfolioGainsAmount,
  portfolioGainsPercentage,
  baseCurrencyCode,
  hideValues,
  baseCurrencySymbol,
  currentRewardTierName,
  togglePortfolioValues
}: {
  isSearching: boolean;
  portfolioValueFormatted: string;
  portfolioGainsAmount: number;
  portfolioGainsPercentage: number;
  showSupportMessageBanner?: boolean;
  isLoading: boolean;
  hideValues: boolean;
  baseCurrencyCode?: string;
  baseCurrencySymbol?: string;
  currentRewardTierName: string;
  togglePortfolioValues: (value: boolean) => void;
}) {
  const localizedContent = useContent(exploreProductsContent);
  const localizedGainLossInfoContent = useContent(gainLossContent);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);

  const shareModal = (
      <div className="mx-0 py-0">
        <div className="justify-start">
          <RewardsShare />
        </div>
      </div>
    );

  return (
    <>
    <div className="flex flex-col px-1 gap-2 items-start justify-start">
      {!isSearching && (
        <>
        <div className="flex flex-row flex-grow w-full">
            <div className="flex flex-row justify-start items-start mr-3">
                {/* REWARDS BADGE SVG */}
                <div
                    style={{
                    width: '4rem',
                    height: '4rem',
                    borderRadius: '4rem',
                    overflow: 'hidden',
                    flexBasis: '4rem',
                }}>
                    <img 
                        className='w-16 h-16' 
                        src={`${import.meta.env.VITE_CDN_URL}/assets/rewards/badges/${currentRewardTierName?.toLowerCase?.()}.png`}
                        alt=''
                    />
                </div>
            </div>
            <div className="flex flex-col justify-start items-start px-0 flex-grow">
                <>
                    <div className="flex flex-row flex-grow justify-between w-full">
                        <div className="flex flex-row flex-grow justify-start">
                            {hideValues ? 
                                <Typography size='large' variant='body' className='text-4xl leading-[2.75rem]'>*******</Typography>
                            :
                                <RollingAmount
                                    amount={portfolioValueFormatted}
                                    isPortfolioAmount={true}
                                    className='items-center'
                                />
                            }
                            
                            <div className="flex flex-col justify-center ml-2">
                                <Pressable
                                    onPress={() => togglePortfolioValues(!hideValues)}
                                    className='mt-2 ml-2'>
                                    {hideValues ? <ShowIcon /> : <HideIcon />}
                                </Pressable>
                            </div>
                        </div>
                        <div className="flex flex-row justify-end items-center self-center">
                            <Button 
                                size="small"
                                color='secondary'
                                onPress={() => setShowShareModal(true)}
                                className="flex flex-row justify-end self-end w-fit h-8 border-0 bg-neutral-600 pr-3 pl-1 py-1">
                                <div className="flex flex-row items-center justify-center rounded-full">
                                    <img src={referDollar} className={'relative h-[20px] w-[20px]'} />
                                    <Typography weight="regular" size="large" className='ml-1'>
                                    {'Refer'}
                                    </Typography>
                                </div>
                            </Button>
                        </div>
                    </div>
                </>
            </div>
        </div>
        <div className="flex flex-col justify-start items-start">
            {portfolioGainsPercentage !== 0 ? (
                <div className='flex flex-row flex-wrap mb-3'>
                    <div className="flex flex-row flex-wrap items-center gap-2">
                    {hideValues ? 
                    <>
                        <GainLoss
                            value={'*****'}
                            symbol={baseCurrencySymbol!}
                            hiddenString={true}
                            hidePrefix={true}
                        />
                        <GainLoss
                            value={'***'}
                            isPercentage
                            background={true}
                            hiddenString={true}
                        />   
                        <div className="flex flex-row flex-wrap gap-2">
                            <Typography>
                                {localizedContent.overTimePeriod['24h']}
                            </Typography>
                            <InfoButton
                                title={localizedGainLossInfoContent.infoTitle}
                                text={localizedGainLossInfoContent.infoText}
                            />
                        </div> 
                    </>
                    :
                    <>
                        <GainLoss
                            value={portfolioGainsAmount ?? 0}
                            symbol={baseCurrencySymbol!}
                        />
                        <GainLoss
                            value={portfolioGainsPercentage ?? 0}
                            background
                        />
                        <div className="flex flex-row flex-wrap gap-2">
                            <Typography>
                                {localizedContent.overTimePeriod['24h']}
                            </Typography>
                            <InfoButton
                                title={localizedGainLossInfoContent.infoTitle}
                                text={localizedGainLossInfoContent.infoText}
                            />
                        </div>
                    </>
                    }
                    </div>
                </div>
            ) : null}
        </div>
        </>
      )}
    </div>

    <Modal
        hideModal={() => setShowShareModal(false)}
        width="auto"
        show={showShareModal}
        noneScrollableContent
        content={shareModal}
        closeModal={() => setShowShareModal(false)}
    />
    </>
  );
}

export default ProductsPortfolio;
